import { useNavigate } from "react-router-dom";
import type { Contract } from "@/lib/api";
import { statusColors, statusLabels, type ContractStatus } from "@/lib/constants";

interface ExpiryTableProps {
  contracts: Contract[];
}

function formatCurrency(value: string | null): string {
  if (!value) return "—";
  return `R ${(parseFloat(value) / 1_000_000).toFixed(1)}M`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" });
}

function daysRemaining(dateStr: string): number {
  const diff = new Date(dateStr).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

export default function ExpiryTable({ contracts }: ExpiryTableProps) {
  const navigate = useNavigate();
  const sorted = [...contracts].sort((a, b) => daysRemaining(a.end_date) - daysRemaining(b.end_date));

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-background-200 bg-background-100">
            {["Supplier", "Department", "Value", "End Date", "Days Left", "Status", "Officer"].map((h, i) => (
              <th key={h} className={`text-xs font-semibold text-foreground-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap ${i >= 2 && i <= 4 ? "text-right" : i === 5 ? "text-center" : "text-left"}`}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((contract) => {
            const remaining = daysRemaining(contract.end_date);
            const st = statusColors[contract.status as ContractStatus] ?? statusColors.active;
            return (
              <tr
                key={contract.id}
                onClick={() => navigate(`/contracts/${contract.id}`)}
                className="border-b border-background-100 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <td className="px-4 py-3">
                  <p className="text-foreground-800 text-sm font-medium">{contract.vendor_name}</p>
                  <p className="text-foreground-400 text-xs">{contract.contract_number}</p>
                </td>
                <td className="px-4 py-3 text-foreground-600 text-sm">{contract.department}</td>
                <td className="px-4 py-3 text-right text-foreground-800 text-sm font-medium">{formatCurrency(contract.contract_value)}</td>
                <td className="px-4 py-3 text-right text-foreground-600 text-sm">{formatDate(contract.end_date)}</td>
                <td className="px-4 py-3 text-right">
                  <span className={`inline-flex items-center justify-center min-w-[44px] h-6 rounded-full text-xs font-semibold ${
                    remaining <= 0 ? "bg-foreground-100 text-foreground-700"
                    : remaining <= 30 ? "bg-foreground-100 text-foreground-700"
                    : remaining <= 60 ? "bg-accent-50 text-accent-700"
                    : remaining <= 90 ? "bg-primary-50 text-primary-700"
                    : "bg-background-100 text-foreground-600"
                  }`}>
                    {remaining <= 0 ? "Lapsed" : `${remaining}d`}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${st.bg} ${st.text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}></span>
                    {statusLabels[contract.status as ContractStatus] ?? contract.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-foreground-600 text-sm">{contract.responsible_officer ?? "—"}</td>
              </tr>
            );
          })}
          {sorted.length === 0 && (
            <tr>
              <td colSpan={7} className="px-4 py-10 text-center text-foreground-400 text-sm">No contracts yet.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
